import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export const statusColors = {
  '-1': 'bg-red-100 text-red-800',    // error
  0: 'bg-gray-100 text-gray-800',      // draft
  1: 'bg-purple-100 text-purple-800',   // ai_review
  2: 'bg-blue-100 text-blue-800',      // human_review
  3: 'bg-yellow-100 text-yellow-800',   // qa
  4: 'bg-green-100 text-green-800',    // finished
  5: 'bg-red-100 text-red-800',        // returned
  6: 'bg-gray-100 text-gray-800'       // archived
};

export const statusNames = {
  '-1': 'Error',
  0: 'Draft',
  1: 'AI Review',
  2: 'Human Review',
  3: 'QA',
  4: 'Finished',
  5: 'Returned',
  6: 'Archived'
};

const AIReviewBadge = ({ startTime }) => {
  return (
    <div className="relative p-[2px] rounded-full bg-gradient-to-r from-purple-800/50 via-transparent via-transparent to-purple-800/50 bg-[length:400%_100%] animate-[gradient-x_6s_linear_infinite] min-w-[120px]">
      <div className="relative inline-flex items-center px-3 py-2 rounded-full text-xs font-medium bg-purple-100 text-purple-800 w-full">
        <Sparkles className="h-4 w-4 mr-2" />
        <span>AI Review</span>
      </div>
    </div>
  );
};

const StatusBadge = ({ status, onStatusChange, startTime, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { hasPermission } = useAuth();

  // Check if user can edit status
  const canChangeStatus = !readOnly && (hasPermission('case_actions', 'update_status') || hasPermission('super_admin'));
  
  // Don't allow status changes during AI Review unless super admin
  const isEditable = canChangeStatus && (status !== 1 || hasPermission('super_admin')); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStatusClick = (e) => {
    if (!isEditable) return;
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleStatusChange = (newStatus, e) => {
    e.stopPropagation();
    setIsOpen(false);
    onStatusChange(newStatus);
  };

  // If no onStatusChange provided, render simple badge
  if (!onStatusChange || readOnly) {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100'}`}>
        {statusNames[status] || 'Unknown'}
      </span>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {status === 1 ? (
        <div 
          className={`${isEditable ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handleStatusClick}
        >
          <AIReviewBadge startTime={startTime} />
        </div>
      ) : (
        <div 
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
            ${statusColors[status] || 'bg-gray-100'}
            ${isEditable ? 'cursor-pointer hover:opacity-80' : ''}`}
          onClick={handleStatusClick}
        >
          <span>{statusNames[status] || 'Unknown'}</span>
          {isEditable && (
            <ChevronDown className="ml-1 h-3 w-3" />
          )}
        </div>
      )}

      {isOpen && isEditable && (
        <div className="fixed mt-1 py-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50" style={{ top: dropdownRef.current?.getBoundingClientRect().bottom + 4, left: dropdownRef.current?.getBoundingClientRect().left }}>
          <div className="py-1" role="menu">
            {Object.entries(statusNames)
              .filter(([id]) => {
                const numId = Number(id);
                // Only super admins can see AI Review in dropdown
                if (numId === 1 && !hasPermission('super_admin')) return false;
                // Only users with specific permissions can move to certain statuses
                if (numId === 4 && !hasPermission('case_actions', 'finalize')) return false;
                if (numId === 3 && !hasPermission('case_actions', 'mark_reviewed')) return false;
                return true;
              })
              .map(([id, name]) => (
                <button
                  key={id}
                  className={`w-full px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2
                    ${Number(id) === status ? 'bg-gray-50' : ''}`}
                  onClick={(e) => handleStatusChange(Number(id), e)}
                >
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[id]}`}>
                    {name}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusBadge; 